import React, { useEffect, useState } from 'react';

import { useSnackbarContext } from '../components/base/snackbar';

import * as GQL from '~/graphql/generated/client';

// Hook with pagination support
export function usePolicyList(
  pagination?: {
    page: number;
    pageSize: number;
  },
  search?: string,
  selectedLabels?: string[] | undefined,
  labelFilter?: string | undefined,
  skip: boolean = false
) {
  const Snackbar = useSnackbarContext();
  // Sanitize selectedLabels: NEString! rejects empty strings, so filter them out
  const safeSelectedLabels = React.useMemo(
    () =>
      selectedLabels?.filter((s) => s != null && String(s).trim() !== '') ??
      undefined,
    [selectedLabels]
  );

  const { data, ...queryResult } = GQL.usePolicyListQuery({
    variables: {
      search: search?.trim() ? search.trim() : undefined,
      labelFilter: labelFilter?.trim() ? labelFilter.trim() : undefined,
      pagination: pagination
        ? {
            page: pagination.page,
            pageSize: pagination.pageSize,
          }
        : undefined,
      selectedLabels: safeSelectedLabels?.length
        ? safeSelectedLabels
        : undefined,
    },
    fetchPolicy: 'cache-first',
    skip,
  });

  React.useEffect(() => {
    if (queryResult.error) {
      Snackbar.open(queryResult.error);
    }
  }, [queryResult.error, Snackbar]);

  const policyList = data?.policyList?.policies ?? [];
  const totalCount = data?.policyList?.totalCount ?? 0;
  const hasNextPage = data?.policyList?.hasNextPage ?? false;
  const hasPreviousPage = data?.policyList?.hasPreviousPage ?? false;

  return {
    policyList,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    ...queryResult,
  };
}

export function usePolicyListAll(labelFilter?: string, skip: boolean = false) {
  const pageSize = 1000;

  const {
    policyList,
    totalCount,
    hasNextPage,
    loading: queryLoading,
    refetch,
  } = usePolicyList(
    { page: 0, pageSize },
    undefined,
    undefined,
    labelFilter,
    skip
  );

  return {
    policyList,
    loading: queryLoading,
    refetch,
  };
}

/** Lightweight list for wizards (no controlBundleList). Use policiesByIds when full policy is needed. */
export function usePolicyListForSelectionAll(
  labelFilter?: string,
  skip: boolean = false
) {
  const pageSize = 1000;
  const Snackbar = useSnackbarContext();
  const safeLabelFilter =
    labelFilter?.trim() && labelFilter.trim() !== ''
      ? labelFilter.trim()
      : undefined;

  // Use the lightweight projection (identifier/name/description/version/labels)
  // — avoids fetching controlBundleList/appliedProjectList/dimensions, which is
  // what made the policy-selection list slow. Full policy is fetched separately
  // via policiesByIds when actually creating the system.
  const { data, ...queryResult } = GQL.usePolicyListForSelectionQuery({
    variables: {
      labelFilter: safeLabelFilter,
      pagination: { page: 0, pageSize },
    },
    fetchPolicy: 'cache-first',
    skip,
  });

  React.useEffect(() => {
    if (queryResult.error) {
      // Silently ignore FORBIDDEN — the caller already skips the query when the
      // user lacks org:view_governance; any race-condition hit just means an
      // empty list, which is the correct UX (no noisy popup for the user).
      const isForbidden = queryResult.error.graphQLErrors?.some(
        (e) => e.extensions?.code === 'FORBIDDEN'
      );
      if (!isForbidden) {
        Snackbar.open(queryResult.error);
      }
    }
  }, [queryResult.error, Snackbar]);

  const policyList = data?.policyList?.policies ?? [];

  return {
    policyList,
    loading: queryResult.loading,
    refetch: queryResult.refetch,
  };
}

export const useDebounce = <T>(value: T, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
};

export function usePolicySearch(searchTerm: string, labelFilter?: string) {
  const debouncedSearchTerm = useDebounce(searchTerm);

  const { data, loading, error } = GQL.usePolicyListQuery({
    variables: {
      search: debouncedSearchTerm,
      labelFilter,
      pagination: { page: 0, pageSize: 50 }, // Get more results for search
    },
    fetchPolicy: 'cache-first',
    skip: debouncedSearchTerm.length === 0,
  });

  const searchResults = data?.policyList?.policies ?? [];

  return {
    searchResults,
    loading,
    error,
  };
}

// Reusable helper to build categories from PolicyInsights
export type PolicyCategory = { name: string; labels: string[] };

export function buildPolicyCategoriesFromInsights(
  policyInsights?: GQL.PolicyInsights | null
): PolicyCategory[] {
  if (!policyInsights) {
    return [];
  }

  const categories: PolicyCategory[] = [
    {
      name: 'Use Case',
      labels: policyInsights.usecases?.map((u) => u.value) || [],
    },
    {
      name: 'Sector',
      labels: policyInsights.sectors?.map((s) => s.value) || [],
    },
    {
      name: 'Methods',
      labels: policyInsights.methods?.map((m) => m.value) || [],
    },
    {
      name: 'Project lifecycle',
      labels: policyInsights.lifecycles?.map((l) => l.value) || [],
    },
    {
      name: 'Jurisdiction',
      labels: policyInsights.jurisdictions?.map((j) => j.value) || [],
    },
  ];

  return categories.filter((category) => category.labels.length > 0);
}

// Custom hook: fetch PolicyInsights and return ready-to-use categories
export function usePolicyInsightCategories() {
  const { data, loading, error } = GQL.usePolicyInsightsQuery({
    fetchPolicy: 'network-only',
  });

  const categories = React.useMemo(
    () => buildPolicyCategoriesFromInsights(data?.policyInsights),
    [data?.policyInsights]
  );

  return { categories, loading, error };
}
